/** Helpers for material product category trees (parent_id hierarchy). */

import type { TreeNode } from '../../core/models/component.model';
import type { MaterialProductCategoryDto } from '@core/shared-bridge';
import {
  PRODUCT_CATEGORY_OBJECT_TYPE_ROOTS,
  PRODUCT_CATEGORY_ROOT_IDS,
  PRODUCT_CATEGORY_ROOT_LABELS,
  type ProductCategoryRootKey,
} from '@core/shared-bridge';
import { productCategoryFilterNodeLabelClass } from './category-tree-table-styles.util';

export { filterItemsByDirectCategory } from '@core/shared-bridge';

export interface ProductCategorySelectOption {
  value: string;
  label: string;
  labelClass: string;
}

export function productCategoryChildrenMap(
  categories: MaterialProductCategoryDto[],
): Map<string | null, MaterialProductCategoryDto[]> {
  const map = new Map<string | null, MaterialProductCategoryDto[]>();
  for (const cat of categories) {
    const key = cat.parentId ?? null;
    const list = map.get(key) ?? [];
    list.push(cat);
    map.set(key, list);
  }
  for (const list of map.values()) {
    list.sort(
      (a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
        a.name.localeCompare(b.name, 'cs', { sensitivity: 'base' }),
    );
  }
  return map;
}

/** Normalized label for matching Obecné / Obecný (drawing vs product category spelling). */
export function normalizeObecneCategoryLabel(label: string | null | undefined): string {
  return String(label ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

export function isObecneCategoryLabel(label: string | null | undefined): boolean {
  const n = normalizeObecneCategoryLabel(label);
  return n === 'obecne' || n === 'obecny';
}

export function isObecneCategory(
  category: Pick<MaterialProductCategoryDto, 'id' | 'name' | 'parentId' | 'objectTypeKey'>,
  obecneRootId = PRODUCT_CATEGORY_ROOT_IDS.OBECNE,
): boolean {
  if (category.objectTypeKey === 'OBECNE') return true;
  if (category.id === obecneRootId) return true;
  return !category.parentId && isObecneCategoryLabel(category.name);
}

/** Resolve Obecné root id from flat categories (constant → objectTypeKey → label). */
export function resolveObecneRootId(
  categories: readonly MaterialProductCategoryDto[],
  fallbackId: string = PRODUCT_CATEGORY_ROOT_IDS.OBECNE,
): string | null {
  if (categories.some((c) => c.id === fallbackId)) return fallbackId;
  const byKey = categories.find((c) => !c.parentId && c.objectTypeKey === 'OBECNE');
  if (byKey) return byKey.id;
  const byLabel = categories.find((c) => !c.parentId && isObecneCategoryLabel(c.name));
  return byLabel?.id ?? null;
}

export function isObecneTreeNode(
  node: Pick<TreeNode, 'label' | 'value'>,
  obecneRootId: string = PRODUCT_CATEGORY_ROOT_IDS.OBECNE,
): boolean {
  const value = String(node.value ?? '').trim();
  if (value && value === obecneRootId) return true;
  return isObecneCategoryLabel(node.label);
}

/** Resolve Obecné root id from `GET /components/tree` nodes (stable UUID or label). */
export function resolveObecneRootIdFromTreeNodes(
  nodes: readonly Pick<TreeNode, 'label' | 'value'>[],
  fallbackId: string = PRODUCT_CATEGORY_ROOT_IDS.OBECNE,
): string {
  for (const node of nodes) {
    if (isObecneTreeNode(node, fallbackId)) {
      const value = String(node.value ?? '').trim();
      return value || fallbackId;
    }
  }
  return fallbackId;
}

function excludeProductCategoryObjectTypeRoots(
  nodes: readonly TreeNode[],
  obecneRootId: string,
): TreeNode[] {
  const objectRootIds = new Set(
    PRODUCT_CATEGORY_OBJECT_TYPE_ROOTS.map((key) => PRODUCT_CATEGORY_ROOT_IDS[key]),
  );
  const objectRootLabels = new Set(
    PRODUCT_CATEGORY_OBJECT_TYPE_ROOTS.map((key) =>
      PRODUCT_CATEGORY_ROOT_LABELS[key].trim().toLowerCase(),
    ),
  );
  return nodes.filter((node) => {
    if (isObecneTreeNode(node, obecneRootId)) return false;
    const value = String(node.value ?? '').trim();
    if (value && objectRootIds.has(value)) return false;
    const label = String(node.label ?? '').trim().toLowerCase();
    return !objectRootLabels.has(label);
  });
}

export function productCategoryPathLabel(
  categories: MaterialProductCategoryDto[],
  categoryId: string | null | undefined,
): string {
  if (!categoryId) return '—';
  const byId = new Map(categories.map((c) => [c.id, c]));
  const parts: string[] = [];
  let current: MaterialProductCategoryDto | undefined = byId.get(categoryId);
  const seen = new Set<string>();
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    parts.unshift(current.name);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return parts.length ? parts.join(' / ') : categoryId.slice(0, 8);
}

/** Flat options with indentation for selects (Obecné root hidden; children promoted). */
export function productCategoryTreeOptions(
  categories: MaterialProductCategoryDto[],
): ProductCategorySelectOption[] {
  const all = [...categories];
  const obecneRootId = resolveObecneRootId(all);
  const obecneChildRoots = obecneRootId ? all.filter((c) => c.parentId === obecneRootId) : [];
  const otherRoots = all.filter(
    (c) => !c.parentId && !isObecneCategory(c, obecneRootId ?? undefined),
  );
  const displayRoots = [...obecneChildRoots, ...otherRoots].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'cs'),
  );
  const byParent = productCategoryChildrenMap(all);
  const options: ProductCategorySelectOption[] = [];

  const walk = (cat: MaterialProductCategoryDto, depth: number) => {
    const indent = depth > 0 ? `${'—'.repeat(depth)} ` : '';
    const children = byParent.get(cat.id) ?? [];
    options.push({
      value: cat.id,
      label: `${indent}${cat.name}`,
      labelClass: productCategoryFilterNodeLabelClass(children.length > 0),
    });
    for (const child of children) {
      walk(child, depth + 1);
    }
  };

  for (const root of displayRoots) {
    walk(root, 0);
  }

  const listed = new Set(options.map((o) => o.value));
  for (const cat of all) {
    if (!listed.has(cat.id) && !isObecneCategory(cat, obecneRootId ?? undefined)) {
      const children = byParent.get(cat.id) ?? [];
      options.push({
        value: cat.id,
        label: cat.name,
        labelClass: productCategoryFilterNodeLabelClass(children.length > 0),
      });
    }
  }
  return options;
}

/**
 * Select options plus the current category when it would otherwise be omitted
 * (Obecné root, orphan, or id not yet in the loaded tree).
 */
export function ensureSelectedCategoryInOptions(
  categories: readonly MaterialProductCategoryDto[],
  selectedId: string | null | undefined,
): ProductCategorySelectOption[] {
  const options = productCategoryTreeOptions([...categories]);
  const id = String(selectedId ?? '').trim();
  if (!id || options.some((o) => o.value === id)) return options;
  const path = productCategoryPathLabel([...categories], id);
  options.push({
    value: id,
    label: path && path !== '—' ? path : id,
    labelClass: productCategoryFilterNodeLabelClass(false),
  });
  return options;
}

/** Self + all descendant category ids. */
export function productCategorySubtreeIds(
  categories: MaterialProductCategoryDto[],
  rootId: string,
): Set<string> {
  const children = productCategoryChildrenMap(categories);
  const out = new Set<string>([rootId]);
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop()!;
    for (const child of children.get(id) ?? []) {
      if (out.has(child.id)) continue;
      out.add(child.id);
      stack.push(child.id);
    }
  }
  return out;
}

export function isInObecneCategorySubtree(
  categories: readonly MaterialProductCategoryDto[],
  categoryId: string | null | undefined,
  obecneRootId?: string | null,
): boolean {
  if (!categoryId) return false;
  const rootId = obecneRootId ?? resolveObecneRootId(categories);
  if (!rootId) return false;
  return productCategorySubtreeIds([...categories], rootId).has(categoryId);
}

/** Resolve systémového kořene produktové kategorie (konstanta → objectTypeKey → název). */
export function resolveProductCategoryRootId(
  categories: readonly MaterialProductCategoryDto[],
  rootKey: ProductCategoryRootKey,
): string | null {
  const constantId = PRODUCT_CATEGORY_ROOT_IDS[rootKey];
  if (categories.some((c) => c.id === constantId)) return constantId;
  const byKey = categories.find((c) => !c.parentId && c.objectTypeKey === rootKey);
  if (byKey) return byKey.id;
  if (rootKey === 'OBECNE') {
    return resolveObecneRootId(categories, constantId);
  }
  const label = PRODUCT_CATEGORY_ROOT_LABELS[rootKey].trim().toLowerCase();
  const byLabel = categories.find(
    (c) => !c.parentId && c.name.trim().toLowerCase() === label,
  );
  return byLabel?.id ?? null;
}

/**
 * Větev vybraného kořene pro filtr produktů: potomci kořene jako top-level uzly
 * (bez uzlu kořene — stejný tvar jako dříve jen pro Obecné).
 */
export function productCategoriesRootSubtree(
  categories: readonly MaterialProductCategoryDto[],
  rootKey: ProductCategoryRootKey = 'OBECNE',
): MaterialProductCategoryDto[] {
  const rootId = resolveProductCategoryRootId(categories, rootKey);
  if (!rootId) return [];
  const ids = productCategorySubtreeIds([...categories], rootId);
  return categories
    .filter((c) => ids.has(c.id) && c.id !== rootId)
    .map((c) => (c.parentId === rootId ? { ...c, parentId: null } : c));
}

/**
 * Filtr / Sklady: jen větev Obecné (kořeny Spojkoviště/Budova/Výkop jsou
 * vidět jen ve správě Produktů → Kategorie; mapa je bere podle object.type).
 * Vrací potomky Obecné jako kořeny stromu (bez uzlu Obecné).
 */
export function productCategoriesObecneSubtree(
  categories: readonly MaterialProductCategoryDto[],
  obecneRootId?: string | null,
): MaterialProductCategoryDto[] {
  if (obecneRootId) {
    const ids = productCategorySubtreeIds([...categories], obecneRootId);
    return categories
      .filter((c) => ids.has(c.id) && c.id !== obecneRootId)
      .map((c) => (c.parentId === obecneRootId ? { ...c, parentId: null } : c));
  }
  return productCategoriesRootSubtree(categories, 'OBECNE');
}

/** Sklady / API component tree: children of Obecné as top-level nodes. */
export function filterComponentTreeToObecne(
  nodes: readonly TreeNode[],
  obecneRootId?: string | null,
): TreeNode[] {
  if (!nodes.length) return [];
  const rootId = obecneRootId ?? resolveObecneRootIdFromTreeNodes(nodes);
  const match = nodes.find((node) => isObecneTreeNode(node, rootId));
  if (match) {
    const children = [...(match.children ?? [])];
    if (children.length > 0) return children;
  }
  return excludeProductCategoryObjectTypeRoots(nodes, rootId);
}

/**
 * Folder browsers: skip Obecné as a navigation step — land inside it.
 * Replaces Obecné with its children; other top-level folders stay visible.
 * No-op when Obecné is missing or has no children to enter.
 */
export function unwrapObecneRootWrapper(
  nodes: readonly TreeNode[],
  obecneRootId?: string | null,
): TreeNode[] {
  if (!nodes.length) return [];
  const rootId = obecneRootId ?? resolveObecneRootIdFromTreeNodes(nodes) ?? PRODUCT_CATEGORY_ROOT_IDS.OBECNE;
  let found = false;
  const out: TreeNode[] = [];
  for (const node of nodes) {
    if (!isObecneTreeNode(node, rootId)) {
      out.push(node);
      continue;
    }
    found = true;
    const children = [...(node.children ?? [])];
    if (children.length > 0) {
      out.push(...children);
    } else {
      out.push(node);
    }
  }
  return found ? out : [...nodes];
}

/**
 * Sklady: Obecné branch for the category panel.
 * Primary = product-category tree; fallback = legacy hlavni_kategorie tree.
 */
export function resolveObecneComponentCatalogTree(
  productCategoryNodes: readonly TreeNode[],
  legacyNodes?: readonly TreeNode[],
): TreeNode[] {
  const fromProduct = filterComponentTreeToObecne(productCategoryNodes);
  if (fromProduct.length > 0) return fromProduct;
  if (!legacyNodes?.length) return [];
  const fromLegacy = filterComponentTreeToObecne(legacyNodes);
  return fromLegacy.length > 0 ? fromLegacy : [...legacyNodes];
}
